
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell, faTrash } from '@fortawesome/free-solid-svg-icons';
import { Client } from "@stomp/stompjs";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import './Navbar.css';
import { GlobalSearchModal } from '../../shared-ui/GlobalSearchModal';
import { AppLauncher } from '../../shared-ui/AppLauncher';
import { fetchTenantBranding, tenantHintFromStorage } from '../../shared-ui/tenantBranding';
import '../../shared-ui/crane-tokens.css';
import notificationSound from '../Sound/notify.mp3';
import { getSwaggerServiceBaseUrl, getNodeServiceBaseUrl } from '../../config/apiBase';
import { getActiveApp } from '../../config/activeModule';

// Derive the notifications WebSocket URL from the Java/Swagger service base
// so it follows the deployed environment instead of a hardcoded dev host.
const buildNotificationsBrokerUrl = () => {
    const httpBase = getSwaggerServiceBaseUrl();
    const wsBase = httpBase.replace(/^http/i, 'ws');
    return `${wsBase}/ws-notifications`;
};

// Create the STOMP client instance ONCE, outside the component.
const stompClient = new Client({
    brokerURL: buildNotificationsBrokerUrl(),
    reconnectDelay: 10000,
    debug: (str) => console.log(`[STOMP DEBUG] ${str}`),
});

const Navbar = () => {
    const [notifications, setNotifications] = useState([]);
    const [isNotificationVisible, setNotificationVisible] = useState(false);
    const [hideTimeoutId, setHideTimeoutId] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [tenantName, setTenantName] = useState('');
    const [tenantLogo, setTenantLogo] = useState(null);
    const subscriptionRef = useRef(null);
    const navigate = useNavigate();
    const brandName = getActiveApp() === 'ZCUSTOMER'
        ? 'ZCustomer'
        : getActiveApp() === 'ZASSET'
            ? 'ZAsset'
            : getActiveApp() === 'ZCOMPLIANCE'
                ? 'ZCompliance'
                : getActiveApp() === 'ZORDER'
                    ? 'ZOrder'
                    : 'ZTask';

    const audioRef = useRef(new Audio(notificationSound));
    const isAudioUnlocked = useRef(false);

    const getToken = useCallback(() => localStorage.getItem('zTaskAuthToken') || '', []);

    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) {
                e.preventDefault();
                setSearchOpen(true);
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    useEffect(() => {
        const hint = tenantHintFromStorage();
        if (hint) setTenantName(hint);

        let cancelled = false;
        fetchTenantBranding().then(({ name, logoUrl }) => {
            if (cancelled) return;
            if (name) setTenantName(name);
            if (logoUrl) setTenantLogo(logoUrl);
        });
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        const unlockAudioContext = () => {
            if (!isAudioUnlocked.current) {
                const audio = audioRef.current;
                audio.muted = true;
                const playPromise = audio.play();

                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        audio.pause();
                        audio.currentTime = 0;
                        audio.muted = false;
                        isAudioUnlocked.current = true;
                        console.log("✅ Audio context unlocked by user interaction.");
                        document.removeEventListener('click', unlockAudioContext);
                        document.removeEventListener('keydown', unlockAudioContext);
                    }).catch(error => {
                        console.error("Audio unlock failed on first attempt:", error);
                    });
                }
            }
        };
        
        document.addEventListener('click', unlockAudioContext);
        document.addEventListener('keydown', unlockAudioContext);

        return () => {
            document.removeEventListener('click', unlockAudioContext);
            document.removeEventListener('keydown', unlockAudioContext);
        };
    }, []);


    const getUserDetails = () => {
        const userDetailsString = localStorage.getItem('zTaskUserDetails');
        if (userDetailsString) {
            try { return JSON.parse(userDetailsString); }
            catch (error) { console.error("Error parsing user details:", error); return null; }
        }
        return null;
    };

    const currentUser = getUserDetails();
    const userId = currentUser ? currentUser.id : null;

    // WebSocket connection logic
    useEffect(() => {
        if (!userId) {
            return;
        }
        
        const onMessageReceived = (message) => {
            console.log('✅✅✅ REAL-TIME NOTIFICATION RECEIVED! ✅✅✅');
            console.log('[WS Real-Time] Raw message body:', message.body);
            
            try {
                const newNotification = JSON.parse(message.body);
                toast.info(newNotification.message, { position: "top-right", autoClose: 3000, theme: "light" });
                
                // Add a unique client-side ID for state management if the server doesn't provide one
                const notificationWithId = {
                    ...newNotification,
                    // Use server-provided notificationId or generate one for key prop
                    id: newNotification.notificationId || `client-${Date.now()}-${Math.random()}` 
                };

                setNotifications(prevNotifications => [notificationWithId, ...prevNotifications]);
                
                if (isAudioUnlocked.current) {
                    audioRef.current.play().catch(error => console.error("[Audio Error] Could not play sound:", error));
                } else {
                    console.warn("[Audio Info] Sound is disabled until first user interaction.");
                }

            } catch (error) {
                console.error("[WS Real-Time] Error processing WebSocket message:", error);
            }
        };

        stompClient.onConnect = () => {
            console.log('✅ [WS Effect] WebSocket Connected!');
            const subscriptionUrl = `/topic/notifications/${userId}`;
            console.log(`[WS Effect] Subscribing to: ${subscriptionUrl}`);
            
            if (subscriptionRef.current) {
                subscriptionRef.current.unsubscribe();
            }
            
            subscriptionRef.current = stompClient.subscribe(subscriptionUrl, onMessageReceived);
        };

        stompClient.onWebSocketClose = (event) => {
            console.log('🔌 [WS Info] WebSocket Connection Closed.', event);
            subscriptionRef.current = null;
        };

        stompClient.onStompError = (frame) => console.error('❌ [WS Error] STOMP protocol error:', frame);
        stompClient.onWebSocketError = (error) => console.error('❌ [WS Error] WebSocket connection error:', error);
        
        stompClient.activate();

        return () => {
            if (subscriptionRef.current) {
                console.log("[WS Cleanup] Unsubscribing from notifications on component unmount.");
                subscriptionRef.current.unsubscribe();
                subscriptionRef.current = null;
            }
        };
    }, [userId]);

    const handleNotificationClick = (taskId) => {
        if (!taskId) {
            console.warn("Notification clicked, but no taskId was provided.");
            return;
        }
        
        navigate('/tasks', { 
            state: { 
                initialFilter: { 
                    taskId: String(taskId),
                    status: []
                } 
            } 
        });
        
        setNotificationVisible(false);
    };

    // --- MODIFIED: Handles marking as seen only on the frontend ---
    const markAsSeen = (notificationId, event) => {
        event.stopPropagation();
        setNotifications(prev => 
            prev.map(notif => 
                notif.id === notificationId ? { ...notif, seen: true } : notif
            )
        );
    };

    // --- MODIFIED: Handles clearing a single notification only on the frontend ---
    const clearNotification = (notificationId, event) => {
        event.stopPropagation();
        setNotifications(prev => 
            prev.filter(n => n.id !== notificationId)
        );
    };

    // --- MODIFIED: Handles clearing all notifications only on the frontend ---
    const clearAllNotifications = () => {
        setNotifications([]);
    };

    const handleMouseEnter = () => {
        if (hideTimeoutId) clearTimeout(hideTimeoutId);
        setNotificationVisible(true);
    };

    const handleMouseLeave = () => {
        const timeoutId = setTimeout(() => { setNotificationVisible(false); }, 300);
        setHideTimeoutId(timeoutId);
    };

    const unreadCount = notifications.filter(n => !n.seen).length;

    return (
        <>
            <ToastContainer />
            <GlobalSearchModal
                open={searchOpen}
                onClose={() => setSearchOpen(false)}
                apiBase={getNodeServiceBaseUrl()}
                getToken={getToken}
            />
            <nav className="navbar-container">
                 <div className="navbar-left" style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                     {/* 9-dot launcher → ZTools → company → global search (flex middle) */}
                     <AppLauncher currentApp={getActiveApp()} />
                     {process.env.REACT_APP_ZTOOL_URL && (
                       <a
                         href={`${String(process.env.REACT_APP_ZTOOL_URL).replace(/\/$/, '')}/dashboard`}
                         className="crane-ztools-back"
                         title="Return to CraneSoftwares launcher"
                       >
                         ← ZTools
                       </a>
                     )}
                     {tenantName ? (
                       <div className="crane-tenant-chip" title="Registered company / tenant">
                         {tenantLogo ? <img src={tenantLogo} alt="" /> : null}
                         <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                           {tenantName}
                         </span>
                       </div>
                     ) : null}
                     <button
                       type="button"
                       className="crane-search-trigger"
                       style={{ flex: 1, minWidth: 160, maxWidth: 480 }}
                       onClick={() => setSearchOpen(true)}
                       aria-label="Open global search"
                     >
                       <span>Global search…</span>
                       <span className="crane-search-trigger__kbd">Ctrl+K</span>
                     </button>
                 </div>
                 {userId && (
                     <div className="navbar-right">
                         <div
                             className="notification-wrapper"
                             onMouseEnter={handleMouseEnter}
                             onMouseLeave={handleMouseLeave}
                         >
                             <button className="navbar-icon-button" type="button" aria-label="Notifications">
                                 <FontAwesomeIcon icon={faBell} />
                                 {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
                             </button>
                             {isNotificationVisible && (
                                 <div className="notification-panel">
                                     <div className="notification-header">
                                         <h3>Notifications</h3>
                                         {notifications.length > 0 && (
                                             <button onClick={clearAllNotifications} className="clear-all-btn">
                                                 Clear All
                                             </button>
                                         )}
                                     </div>
                                     <div className="notification-list">
                                         {notifications.length > 0 ? (
                                             notifications.map((notif) => (
                                                 <div
                                                     key={notif.id}
                                                     className={`notification-item ${notif.seen ? 'notification-item--seen' : ''} notification-item--clickable`}
                                                     onClick={() => handleNotificationClick(notif.taskId)}
                                                 >
                                                     <div className="notification-content">
                                                         <p>{notif.message}</p>
                                                         <span className="notification-timestamp">
                                                             {new Date(notif.timeStamp).toLocaleString()}
                                                         </span>
                                                     </div>
                                                     <div className="notification-actions">
                                                         {!notif.seen && (
                                                             <button onClick={(e) => markAsSeen(notif.id, e)} className="mark-as-read-btn">
                                                                 Mark as Read
                                                             </button>
                                                         )}
                                                         <button onClick={(e) => clearNotification(notif.id, e)} className="delete-notification-btn">
                                                             <FontAwesomeIcon icon={faTrash} />
                                                         </button>
                                                     </div>
                                                 </div>
                                             ))
                                         ) : (
                                             <div className="notification-item-empty"><p>No new notifications</p></div>
                                         )}
                                     </div>
                                 </div>
                             )}
                         </div>
                         <div className="navbar-user-profile" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ textAlign: 'right', lineHeight: 1.2 }}>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{currentUser?.name || 'User'}</div>
                              <div style={{ fontSize: 11, color: '#3f4850' }}>{currentUser?.role?.name || brandName}</div>
                            </div>
                            <div className="avatar" style={{ width: 32, height: 32, borderRadius: '50%', background: '#006194', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 13 }}>
                              {(currentUser?.name || 'U').charAt(0).toUpperCase()}
                            </div>
                         </div>
                     </div>
                 )}
             </nav>
        </>
    );
};

export default Navbar;